import { Flex, Box, Spinner, Text } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { LocationStore } from '../../location-store';
import { UnverifiedAddressIndicator } from './unverified-address-indicator';
import { LocationList } from '../location-list/location-list';
import { LocationStoreView } from '../../types/locations';
import { LocationMapListItem } from './location-map-list-item';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { useLocationList } from '../../hooks/use-location-list';
import React from 'react';
import { CommonSize, getTwoSizesSmaller } from '../../../common/helpers/utils';

const LocationMapContainerV1 = observer(function LocationMapContainer(props: {
    store: LocationStore;
    isLoadingResults: boolean;
    showUnverifiedAddressIndicator: boolean;
    children: React.ReactNode;
    query: string;
    onClickRow?: (id: number) => void;
    inputRef?: React.MutableRefObject<HTMLInputElement | null>;
    moveFocusNext?: () => void;
    onLocationSelected: (
        location: LocationStoreView,
        index: number,
        event: { source: 'keyboard' | 'mouse' }
    ) => void;
    showMapKey?: boolean;
    size?: CommonSize;
}) {
    const {
        store,
        isLoadingResults,
        showUnverifiedAddressIndicator,
        children,
        query,
        onClickRow,
        inputRef,
        moveFocusNext,
        onLocationSelected,
        showMapKey = true,
        size,
    } = props;

    const smallerSize = getTwoSizesSmaller(size);

    const { mark43Items, esriItems, esriCustomItems, latLngItems, forcedResults } = useLocationList(
        {
            onClickRow,
            inputRef,
            moveFocusNext,
            store,
        }
    ).data;

    return (
        <Flex h="350px" flexGrow={1} data-testid="location-map">
            <Box w="45%">
                {isLoadingResults ? (
                    <Flex w="100%" h="100%" alignItems="center" justifyContent="center">
                        <Spinner />
                    </Flex>
                ) : (
                    <Box w="100%" h="100%" overflowY="auto">
                        {esriCustomItems.length > 0 ? (
                            <>
                                {esriCustomItems.map((item, index) => (
                                    <LocationMapListItem
                                        store={store}
                                        query={query}
                                        key={item.uuid}
                                        index={index}
                                        location={item}
                                        onClickRow={onClickRow}
                                        onLocationSelected={onLocationSelected}
                                        moveFocusNext={moveFocusNext}
                                        inputRef={inputRef}
                                        showMapKey={showMapKey}
                                        size={smallerSize}
                                    />
                                ))}
                            </>
                        ) : undefined}
                        {mark43Items.length > 0 ? (
                            <>
                                {mark43Items.map((item, index) => (
                                    <LocationMapListItem
                                        store={store}
                                        query={query}
                                        key={item.uuid}
                                        index={index + esriCustomItems.length}
                                        location={item}
                                        onClickRow={onClickRow}
                                        onLocationSelected={onLocationSelected}
                                        moveFocusNext={moveFocusNext}
                                        inputRef={inputRef}
                                        showMapKey={showMapKey}
                                        size={smallerSize}
                                    />
                                ))}
                            </>
                        ) : undefined}
                        {esriItems.length > 0 ? (
                            <>
                                <Box pl={3} pt={3} color="brand.default" bg="surface.primary">
                                    <Text fontSize={smallerSize}>Results from ESRI</Text>
                                </Box>
                                {esriItems.map((item, index) => (
                                    <LocationMapListItem
                                        store={store}
                                        query={query}
                                        key={item.uuid}
                                        location={item}
                                        onClickRow={onClickRow}
                                        onLocationSelected={onLocationSelected}
                                        moveFocusNext={moveFocusNext}
                                        inputRef={inputRef}
                                        showMapKey={showMapKey}
                                        index={index + esriCustomItems.length + mark43Items.length}
                                        size={smallerSize}
                                    />
                                ))}
                            </>
                        ) : undefined}
                        {latLngItems.length > 0 ? (
                            <>
                                {latLngItems.map((item, index) => (
                                    <LocationMapListItem
                                        store={store}
                                        query={query}
                                        key={item.uuid}
                                        location={item}
                                        onClickRow={onClickRow}
                                        onLocationSelected={onLocationSelected}
                                        moveFocusNext={moveFocusNext}
                                        inputRef={inputRef}
                                        showMapKey={showMapKey}
                                        index={
                                            index +
                                            esriCustomItems.length +
                                            mark43Items.length +
                                            esriItems.length
                                        }
                                        size={smallerSize}
                                    />
                                ))}
                            </>
                        ) : undefined}
                        {forcedResults.map((row, index) => {
                            return (
                                <LocationMapListItem
                                    store={store}
                                    query={query}
                                    key={row.id}
                                    index={
                                        index +
                                        esriCustomItems.length +
                                        mark43Items.length +
                                        esriItems.length +
                                        latLngItems.length
                                    }
                                    location={row}
                                    onClickRow={onClickRow}
                                    onLocationSelected={onLocationSelected}
                                    moveFocusNext={moveFocusNext}
                                    inputRef={inputRef}
                                    showMapKey={showMapKey}
                                    size={smallerSize}
                                />
                            );
                        })}
                    </Box>
                )}
            </Box>
            <Box data-testid="location-select-map" w="55%" overflow="hidden" position="relative" bg="surface.foreground">
                {showUnverifiedAddressIndicator && <UnverifiedAddressIndicator />}
                {children}
            </Box>
        </Flex>
    );
});

const _LocationMapContainer = observer(function LocationMapContainer(props: {
    store: LocationStore;
    isLoadingResults: boolean;
    showUnverifiedAddressIndicator: boolean;
    children: React.ReactNode;
    query: string;
    showMapKey?: boolean;
    onClickRow?: (id: number) => void;
    isCliMode?: boolean;
    size?: CommonSize;
}) {
    const {
        store,
        isLoadingResults,
        showUnverifiedAddressIndicator,
        children,
        query,
        showMapKey = true,
        onClickRow,
        isCliMode,
        size,
    } = props;

    return (
        <Flex h={isCliMode ? '350px' : '100%'} flexGrow={1} data-testid="location-map">
            <Box w="45%">
                <LocationList
                    store={store}
                    query={query}
                    isLoadingResults={isLoadingResults}
                    mode={showMapKey ? 'MAP' : 'LIST'}
                    onClickRow={onClickRow}
                    size={size}
                />
            </Box>
            <Box data-testid="location-select-map" w="55%" overflow="hidden" position="relative" bg="surface.foreground">
                {showUnverifiedAddressIndicator && <UnverifiedAddressIndicator />}
                {children}
            </Box>
        </Flex>
    );
});

export const LocationMapContainer = observer(function LocationMapContainer(props: {
    store: LocationStore;
    isLoadingResults: boolean;
    showUnverifiedAddressIndicator: boolean;
    children: React.ReactNode;
    query: string;
    onClickRow?: (id: number) => void;
    inputRef?: React.MutableRefObject<HTMLInputElement | null>;
    moveFocusNext?: () => void;
    onLocationSelected: (
        location: LocationStoreView,
        index: number,
        event: { source: 'keyboard' | 'mouse' }
    ) => void;
    showMapKey?: boolean;
    isCliMode?: boolean;
    size?: CommonSize;
}) {
    const { subpremiseSupport } = useEnabledFeatures();

    if (subpremiseSupport) {
        return <_LocationMapContainer {...props} />;
    }
    return <LocationMapContainerV1 {...props} />;
});
